import { SessionSelectors } from '@next/core-lib/session';
import { createSelector, createFeatureSelector } from '@ngrx/store';
import { getSelectedFeaturesIds } from 'src/app/geo/store/selectors/feature.selectors';

import {
  CropFieldState,
  DetailedCropField,
  CropField,
  ArableSessionState,
  GroupedCropFields,
} from 'src/app/shared/models';

import { getCropsMap } from './crop.selectors';
import { adapter } from '../reducers/crop-field.reducer';

const { selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const getCropFieldState = createFeatureSelector<CropFieldState>('crop-field');

export const getCropFields = createSelector(getCropFieldState, selectAll);
export const getCropFieldEntities = createSelector(getCropFieldState, selectEntities);

export const getCropField = (props: { id: string }) =>
  createSelector(
    getCropFieldEntities,
    (cropFields: { [key: string]: CropField }) => cropFields[props.id],
  );

export const getDetailedCropFields = createSelector(
  getCropFields,
  getCropsMap,
  (cropFields, crops): DetailedCropField[] =>
    cropFields.map((cropField) => ({
      ...cropField,
      cropName: crops[cropField.cropId.toString()],
    })),
);

export const getGroupedCropFields = createSelector(
  getDetailedCropFields,
  (cropFields): GroupedCropFields => {
    const mainCultivations = cropFields.filter((cropField) => !cropField.isSubsequentCultivation);
    const subsequentCultivations = cropFields.filter(
      (cropField) => cropField.isSubsequentCultivation,
    );

    const mainCropFieldMap = mainCultivations.reduce<{ [key: string]: DetailedCropField[] }>(
      (acc, curr) => ({ ...acc, [curr.cropId]: [...(acc[curr.cropId] || []), curr] }),
      {},
    );
    const subsequentCropFieldMap = subsequentCultivations.reduce<{
      [key: string]: DetailedCropField[];
    }>((acc, curr) => ({ ...acc, [curr.cropId]: [...(acc[curr.cropId] || []), curr] }), {});

    return {
      mainCultivations: Object.keys(mainCropFieldMap)
        .map((cropId) => ({
          cropName: mainCropFieldMap[cropId][0].cropName,
          totalHectares: mainCropFieldMap[cropId].reduce(
            (acc, curr) => acc + curr.areaInHectares,
            0,
          ),
          cropFields: mainCropFieldMap[cropId].sort((a, b) =>
            a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
          ),
        }))
        .sort((a, b) => (a.cropName < b.cropName ? -1 : a.cropName > b.cropName ? 1 : 0)),
      mainCultivationsTotalFields: mainCultivations.length,
      mainCultivationsTotalHa: mainCultivations.reduce((acc, curr) => acc + curr.areaInHectares, 0),
      subsequentCultivations: Object.keys(subsequentCropFieldMap)
        .map((cropId) => ({
          cropName: subsequentCropFieldMap[cropId][0].cropName,
          totalHectares: subsequentCropFieldMap[cropId].reduce(
            (acc, curr) => acc + curr.areaInHectares,
            0,
          ),
          cropFields: subsequentCropFieldMap[cropId].sort((a, b) =>
            a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
          ),
        }))
        .sort((a, b) => (a.cropName < b.cropName ? -1 : a.cropName > b.cropName ? 1 : 0)),
      subsequentCultivationsTotalFields: subsequentCultivations.length,
      subsequentCultivationsTotalHa: subsequentCultivations.reduce(
        (acc, curr) => acc + curr.areaInHectares,
        0,
      ),
    };
  },
);

export const getSelectedCropFieldsIds = createSelector(
  getCropFields,
  getSelectedFeaturesIds,
  (cropFields, ids) =>
    cropFields
      .filter((cropField) => ids.indexOf(cropField.id.toString()) !== -1)
      .map((cropField) => cropField.id),
);

export const getFilteredCropFieldsIds = createSelector(
  getCropFieldState,
  (state: CropFieldState) => state.filteredIds,
);

export const getSelectedCropFields = createSelector(
  [getCropFields, getSelectedCropFieldsIds],
  (cropFields, cropFieldsIds) =>
    (cropFields as CropField[]).filter((cropField) =>
      (cropFieldsIds as number[]).includes(cropField.id),
    ),
);

export const hasSelectedCropFieldsIds = createSelector(
  getSelectedCropFieldsIds,
  (cropFields) => cropFields.length > 0,
);

export const getSelectedDetailedCropFields = createSelector(
  [getDetailedCropFields, getSelectedCropFieldsIds],
  (detailedCropFields, cropFieldsIds) =>
    (detailedCropFields as DetailedCropField[]).filter((cropField) =>
      (cropFieldsIds as number[]).includes(cropField.id),
    ),
);

export const hasSelectedCropFieldsParticipatingInBem = createSelector(
  getSelectedDetailedCropFields,
  (selectedDetailedCropFields) =>
    selectedDetailedCropFields.some(
      (cropField) => cropField.isParticipatingToBem === true && cropField.isCancellingBem === false,
    ),
);
export const numberOfCropFields = createSelector(getCropFieldState, selectTotal);

export const getLastYearsEntities = createSelector(
  getCropFieldState,
  (store) => store.entitiesLastYear || [],
);

export const selectArableSession = createSelector(
  SessionSelectors.selectSessionState,
  (session: ArableSessionState) => session?.arable,
);

export const isPrefilling = createSelector(
  getCropFieldState,
  (store) => store.isPrefilling || false,
);

export const getAgroConnectCustomers = createSelector(
  getCropFieldState,
  (store) => store.agroConnectCustomers ?? [],
);

export const isAwaitingSync = createSelector(
  getCropFieldState,
  (store) => store.isAwaitingSync ?? false,
);

export const getSyncId = createSelector(getCropFieldState, (store) => store.syncId ?? new Date());
